import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bike, Check, MessageCircle, Gauge, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import PickupMapCard from "@/components/PickupMapCard";
import DroneAddon from "@/components/DroneAddon";
import { api, waLink } from "@/lib/api";
import { photoSrc } from "@/components/Reviews";

function BookingDialog({ vehicle, onClose }) {
  const [date, setDate] = useState(null);
  const [hours, setHours] = useState(24);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState([]);

  useEffect(() => {
    setDate(null);
    setBooked([]);
    if (vehicle) api.get(`/vehicles/${vehicle.id}/booked-dates`).then((r) => setBooked(r.data.dates)).catch(() => {});
  }, [vehicle]);

  if (!vehicle) return null;
  const price = hours === 12 ? vehicle.rate_12hr : vehicle.rate_24hr;
  const dateStr = date ? date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
  const inputCls = "w-full bg-sand border border-ink/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50";
  const bookedDates = booked.map((d) => new Date(`${d}T00:00:00`));

  const submit = async () => {
    if (!date) return toast.error("Pick a start date first");
    if (!name.trim() || !phone.trim()) return toast.error("Add your name and phone number");
    setBusy(true);
    const summary = `Rental booking: ${vehicle.name} — ${hours} hr hire starting ${dateStr}. Fare ₹${price}. Name: ${name}, Phone: ${phone}`;
    const isoDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    try {
      await api.post("/enquiries", {
        name, phone, service: "Scooter / Bike Rental", travel_dates: dateStr, message: summary,
        kind: "rental", vehicle_id: vehicle.id, vehicle_name: vehicle.name, booking_date: isoDate, duration_hours: hours, days: 1, estimate: price,
      });
      toast.success("Booking request saved! Opening WhatsApp to confirm...");
      window.open(waLink(`Hi Random Rider! Booking the ${vehicle.name} for ${hours} hrs on ${dateStr} (₹${price}). Name: ${name}`), "_blank");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save booking. Please try WhatsApp directly.");
      if (err?.response?.status === 409) api.get(`/vehicles/${vehicle.id}/booked-dates`).then((r) => setBooked(r.data.dates)).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!vehicle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-md bg-sand border-ink/10" data-testid="rental-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">Book the {vehicle.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-2">Start date</p>
            <div className="bg-white rounded-xl border border-ink/10 overflow-hidden flex justify-center" data-testid="rental-booking-calendar">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={[{ before: new Date() }, ...bookedDates]}
                modifiers={{ booked: bookedDates }} modifiersClassNames={{ booked: "line-through text-red-500 opacity-70" }} />
            </div>
            <p className="mt-1.5 text-[11px] text-ink/50 flex items-center gap-1.5" data-testid="rental-booked-legend">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> {booked.length ? `Struck-out dates are already booked for this ${vehicle.type}` : `All upcoming dates are free for this ${vehicle.type}`}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-ink/60 mb-2">Duration</p>
            <div className="grid grid-cols-2 gap-3">
              {[12, 24].map((h) => (
                <button key={h} type="button" onClick={() => setHours(h)} data-testid={`rental-duration-${h}hr-button`}
                  className={`rounded-xl border px-4 py-3 text-center transition-colors ${hours === h ? "border-terra bg-terra/10" : "border-ink/15 bg-white hover:border-terra/50"}`}>
                  <p className="font-serif text-xl font-bold text-ink">₹{h === 12 ? vehicle.rate_12hr : vehicle.rate_24hr}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-ink/50">{h} hours</p>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="rental-booking-name-input" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className={inputCls} data-testid="rental-booking-phone-input" />
          </div>
          <button onClick={submit} disabled={busy} data-testid="rental-booking-confirm-button"
            className="w-full flex items-center justify-center gap-2 bg-terra text-white font-semibold py-3 rounded-full hover:bg-ink transition-colors disabled:opacity-60">
            <CalendarDays size={16} /> {busy ? "Saving..." : `Confirm · ₹${price} on ${dateStr || "pick a date"}`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Rentals() {
  const [vehicles, setVehicles] = useState([]);
  const [booking, setBooking] = useState(null);
  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  return (
    <section id="rentals" className="py-20 lg:py-28 bg-sand text-ink relative overflow-hidden" data-testid="rentals-section">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-terra/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mb-12"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra mb-3">05 · Two-Wheeler Fleet</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Ride Puri on <span className="text-terra italic">your own</span> terms
          </h2>
          <p className="mt-4 text-ink/70 leading-relaxed">
            Royal Enfields for the marine drive, scooties for temple lanes — free helmets, 12-hour and 24-hour hires, doorstep delivery to your hotel.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {vehicles.map((v, i) => {
            const slug = v.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
            return (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -8 }}
                className="group bg-white border border-ink/10 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-terra/40 transition-all duration-300"
                data-testid={`rental-card-${slug}`}
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={photoSrc(v.image)} alt={v.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-3 left-3 bg-ink/80 backdrop-blur text-terra text-[11px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full flex items-center gap-1">
                    <Bike size={11} /> {v.type}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl font-bold leading-tight">{v.name}</h3>
                  <div className="mt-3 flex gap-3" data-testid={`rental-rate-${slug}`}>
                    <div className="flex-1 bg-ink/5 rounded-xl px-3 py-2 text-center">
                      <p className="font-serif text-2xl font-black text-terra">₹{v.rate_12hr}</p>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-ink/60">12 hours</p>
                    </div>
                    <div className="flex-1 bg-terra/20 border border-terra/40 rounded-xl px-3 py-2 text-center">
                      <p className="font-serif text-2xl font-black text-ink">₹{v.rate_24hr}</p>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-ink/60">24 hours</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {v.specs?.map((s) => (
                      <span key={s} className="text-[11px] bg-ink/5 text-ink/80 px-2 py-1 rounded-full flex items-center gap-1">
                        <Gauge size={10} /> {s}
                      </span>
                    ))}
                  </div>
                  <ul className="mt-3 space-y-1">
                    {v.features?.map((f) => (
                      <li key={f} className="flex items-center gap-1.5 text-xs text-ink/70">
                        <Check size={12} className="text-terra shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => setBooking(v)}
                      data-testid={`rental-book-${slug}`}
                      className="flex-1 flex items-center justify-center gap-2 bg-terra text-white text-sm font-semibold py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      <CalendarDays size={15} /> Pick dates
                    </button>
                    <a
                      href={waLink(`Hi Random Rider! I want to rent the ${v.name} (₹${v.rate_12hr}/12hr or ₹${v.rate_24hr}/24hr) in Puri.`)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`rental-whatsapp-${slug}`}
                      aria-label={`Book ${v.name} on WhatsApp`}
                      className="w-11 flex items-center justify-center bg-[#25D366] text-white rounded-full hover:brightness-110 transition-all"
                    >
                      <MessageCircle size={16} />
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        <DroneAddon />
        <PickupMapCard />
      </div>
      <BookingDialog vehicle={booking} onClose={() => setBooking(null)} />
    </section>
  );
}
